import React, { useEffect, useMemo } from 'react';
import { StepComponent } from './StepComponent';
import {
    HelpType,
    ResponsiveValue,
    hideHelp,
    setCurrentMessageIndex,
} from 'store/slices/help';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getResponsiveValue } from 'hooks/useHelpOverlayResponsiveValues';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export interface Step {
    message: ResponsiveValue<string>;
    type: HelpType;
    shape: 'circle' | 'square';
    visible?: ResponsiveValue<boolean>;
    onShow?: () => void;
    onHide?: () => void;
    onPrev?: () => void;
    onNext?: () => void;
}

interface Props {
    steps: Step[];
    index: number;
    setIndex: (index: number) => void;
    onClose: () => void;
    onShapeChange: (shape: 'circle' | 'square') => void;
    onClickHoleRef: React.MutableRefObject<() => void>;
}

export const NavigableStepComponent: React.FC<Props> = ({
    steps,
    index,
    setIndex,
    onClose,
    onShapeChange,
    onClickHoleRef
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const matches = {
        xs: useMediaQuery(theme.breakpoints.down('sm')),
        sm: useMediaQuery(theme.breakpoints.down('md')),
        md: useMediaQuery(theme.breakpoints.down('lg')),
        lg: useMediaQuery(theme.breakpoints.down('xl')),
        xl: true,
    };

    const visibilityMap = useMemo(
        () => steps.map((step) => getResponsiveValue(matches, step.visible ?? true)),
        [steps, matches.xs, matches.sm, matches.md, matches.lg, matches.xl]
    );

    const current = steps[index];
    const isCurrentVisible = visibilityMap[index];
    const hasNext = visibilityMap.slice(index + 1).some(Boolean);
    const isFirst = visibilityMap.slice(0, index).reverse().find((v) => v) === undefined;

    const goToNextVisible = () => {
        for (let i = index + 1; i < visibilityMap.length; i++) {
            if (visibilityMap[i]) {
                current.onNext?.();
                setIndex(i);
                return;
            }
        }
    };

    const goToPrevVisible = () => {
        for (let i = index - 1; i >= 0; i--) {
            if (visibilityMap[i]) {
                current.onPrev?.();
                setIndex(i);
                return;
            }
        }
    };

    useEffect(() => {
        if (isCurrentVisible) return;

        for (let i = index + 1; i < visibilityMap.length; i++) {
            if (visibilityMap[i]) {
                setIndex(i);
                return;
            }
        }

        for (let i = index - 1; i >= 0; i--) {
            if (visibilityMap[i]) {
                setIndex(i);
                return;
            }
        }

        onClose();
        dispatch(hideHelp());
    }, [visibilityMap, index]);

    useEffect(() => {
        if (!isCurrentVisible) return;

        current.onShow?.();
        onShapeChange(current.shape);
        dispatch(setCurrentMessageIndex(index));

        return () => {
            current.onHide?.();
        };
    }, [index, isCurrentVisible]);

    useEffect(() => {
        if (onClickHoleRef) {
            onClickHoleRef.current = goToNextVisible;
        }
    }, [index, visibilityMap, onClickHoleRef]);

    if (!isCurrentVisible) return null;

    return (
        <StepComponent
            message={current.message}
            type={current.type}
            stepNumber={visibilityMap.slice(0, index + 1).filter(Boolean).length}
            totalSteps={visibilityMap.filter(Boolean).length}
            hasNext={hasNext}
            isFirst={isFirst}
            onPrev={goToPrevVisible}
            onNext={goToNextVisible}
            onClose={() => {
                onClose();
                dispatch(hideHelp());
            }}
        />
    );
};
